import { useMemo } from 'react';
import { formatBidNumber } from '../utils/bidNumber';
import { parseWallClock } from '../utils/datetime';
import '../styles/pipeline.css';

const STAGES = [
  { status: 'Pipeline', color: '#06b6d4', bg: '#cffafe' },
  { status: 'Budget', color: '#8b5cf6', bg: '#ede9fe' },
  { status: 'Bidding', color: 'var(--color-accent)', bg: '#dbeafe' },
  { status: 'Pending', color: '#f59e0b', bg: '#fef3c7' },
  { status: 'Awarded', color: '#16a34a', bg: '#dcfce7' },
  { status: 'Not Awarded', color: '#dc2626', bg: '#fee2e2' },
];

function formatCurrency(val) {
  if (!val || val === 0) return '';
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = parseWallClock(dateStr);
  const now = new Date();
  return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
}

export default function PipelineBoard({ projects, navigate }) {
  const columns = useMemo(() => {
    return STAGES.map(stage => ({
      ...stage,
      projects: projects
        .filter(p => p.StatusName === stage.status)
        .sort((a, b) => {
          const da = parseWallClock(a.GCOwnerDeadline || a.SubDueDate || a.ITBReceived);
          const db = parseWallClock(b.GCOwnerDeadline || b.SubDueDate || b.ITBReceived);
          return (da || 0) - (db || 0);
        }),
    }));
  }, [projects]);

  return (
    <div className="pipeline-board">
      {columns.map(col => (
        <div key={col.status} className="pipeline-column">
          <div className="pipeline-column-header" style={{ borderTopColor: col.color }}>
            <h4>{col.status === 'Pipeline' ? 'Opportunities' : col.status}</h4>
            <div className="pipeline-column-meta">
              <span className="pipeline-count">{col.projects.length}</span>
              <span className="pipeline-value">
                {formatCurrency(col.projects.reduce((s, p) => s + (p.ProjectCost || 0), 0))}
              </span>
            </div>
          </div>
          <div className="pipeline-cards">
            {col.projects.map(p => {
              const deadline = p.GCOwnerDeadline || p.InternalDeadline || p.SubDueDate;
              const days = daysUntil(deadline);
              const urgency = days !== null && days <= 2 ? 'urgent' : days !== null && days <= 5 ? 'warning' : '';
              return (
                <div key={p.ProjectId} className={`pipeline-card ${urgency} ${p.IsPriority ? 'cm-priority-row' : ''}`} onClick={() => navigate(`/projects/${p.ProjectId}`)}>
                  <div className="pipeline-card-header">
                    <span className="pipeline-card-bid">{formatBidNumber(p.BidNumber, p.RevisionNumber)}</span>
                    {p.IsPriority && <span className="cm-must-win-tag">Must Win</span>}
                    {p.ProjectCost > 0 && <span className="pipeline-card-value">{formatCurrency(p.ProjectCost)}</span>}
                  </div>
                  <div className="pipeline-card-name">{p.ProjectName}</div>
                  <div className="pipeline-card-footer">
                    <span className="pipeline-card-estimator">{p.Estimator || '-'}</span>
                    {days !== null && (
                      <span className={`pipeline-card-deadline ${urgency}`}>
                        {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Today' : `${days}d`}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {col.projects.length === 0 && (
              <div className="pipeline-empty">No projects</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
